import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Mail, Phone } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';

export default function ContactPage() {
  const { t } = useLanguage();
  const { company } = useOutletContext() || {};
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/public/contact', form);
      toast.success(t('contact.success'));
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-xs uppercase tracking-widest text-[hsl(var(--brand-gold))]">Contact</div>
      <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[hsl(var(--brand-navy-2))]">{t('contact.title')}</h1>
      <p className="mt-4 text-lg text-[hsl(var(--muted-foreground))]">{t('contact.subtitle')}</p>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
        <div className="lg:col-span-7">
          <Card className="bg-white">
            <CardContent className="p-6">
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>{t('contact.form_name')}</Label><Input required data-testid="contact-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>{t('contact.form_email')}</Label><Input required type="email" data-testid="contact-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                </div>
                <div><Label>{t('contact.form_subject')}</Label><Input data-testid="contact-subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} /></div>
                <div><Label>{t('contact.form_message')}</Label><Textarea required rows={5} data-testid="contact-message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} /></div>
                <Button type="submit" disabled={busy} data-testid="contact-submit-button" className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]">{t('contact.form_submit')}</Button>
              </form>
            </CardContent>
          </Card>
        </div>
        <div className="lg:col-span-5 space-y-4">
          <Card className="bg-white"><CardContent className="p-6">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-[hsl(var(--brand-gold))]" />
              <div>
                <div className="font-semibold">Registered office</div>
                <div className="text-sm text-[hsl(var(--muted-foreground))]">{company?.address}</div>
              </div>
            </div>
          </CardContent></Card>
          <Card className="bg-white"><CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-[hsl(var(--brand-gold))]" />
              <div>
                <div className="font-semibold">Email</div>
                <a href={`mailto:${company?.email}`} className="text-sm text-[hsl(var(--brand-navy))] hover:underline">{company?.email}</a>
              </div>
            </div>
          </CardContent></Card>
          <Card className="bg-white"><CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-[hsl(var(--brand-gold))]" />
              <div>
                <div className="font-semibold">Phone</div>
                <div className="text-sm">{company?.phone}</div>
              </div>
            </div>
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}
